import type { Game2048Appearance } from '../../lib/game2048Appearance';
import { cn } from '../../lib/utils';

const tileClasses: Record<number, string> = {
  2: 'bg-stone-100 dark:bg-stone-600 text-gray-800 dark:text-white',
  4: 'bg-amber-100 dark:bg-amber-700 text-gray-800 dark:text-white',
  8: 'bg-orange-300 text-gray-900',
  16: 'bg-orange-400 text-gray-900',
  32: 'bg-orange-500 text-gray-950',
  64: 'bg-red-500 text-gray-950',
  128: 'bg-yellow-400 text-gray-900',
  256: 'bg-yellow-500 text-gray-900',
  512: 'bg-yellow-600 text-gray-950',
  1024: 'bg-teal-700 text-white',
  2048: 'bg-indigo-600 text-white',
  4096: 'bg-purple-600 text-white',
  8192: 'bg-tile-bronze bg-[linear-gradient(135deg,#F7D7B5_0%,#B87333_48%,#E8B88B_100%)] text-[#2D1607] ring-1 ring-inset ring-white/70 shadow-[inset_0_-3px_0_#8B4F24]',
  16384: 'bg-tile-silver bg-[linear-gradient(145deg,#FFFFFF_0%,#CBD5E1_48%,#94A3B8_100%)] text-slate-950 ring-1 ring-inset ring-white shadow-[inset_0_-3px_0_#64748B]',
  32768: 'bg-tile-gold bg-[linear-gradient(115deg,#FACC15_25%,#FEF3C7_45%,#FACC15_65%)] bg-[length:250%_100%] text-yellow-950 ring-2 ring-inset ring-yellow-100 animate-tile-gold motion-reduce:animate-none shadow-[inset_0_-3px_0_#A16207]',
  65536: 'bg-[linear-gradient(135deg,#F43F5E_0%,#F59E0B_20%,#4ADE80_40%,#38BDF8_60%,#818CF8_80%,#E879F9_100%)] bg-[length:250%_100%] animate-tile-gold motion-reduce:animate-none motion-reduce:bg-[length:100%_100%] text-white ring-1 ring-inset ring-white/80',
  131072: 'bg-tile-paper text-tile-ink ring-2 ring-inset ring-tile-ink outline outline-1 -outline-offset-[6px] outline-tile-ink'
};

export const Game2048TileFace = ({ value, appearance }: { value: number; appearance?: Game2048Appearance }) => {
  const surface = tileClasses[value];
  if (!surface) {
    throw new Error('Invalid 2048 tile face: ' + value);
  }
  if (appearance && value !== 131072) {
    return <PictureTileFace value={value} appearance={appearance} />;
  }
  return <NumberTileFace value={value} surface={surface} />;
};

const PictureTileFace = ({ value, appearance }: { value: number; appearance: Game2048Appearance }) => {
  const src = appearance.images[value];
  if (!src) {
    throw new Error('Missing 2048 image: ' + value);
  }
  return (
    <div data-tile-value={value} aria-hidden="true" className="absolute inset-0 overflow-hidden rounded-md shadow-sm">
      <img
        src={src}
        alt=""
        draggable={false}
        className={cn('h-full w-full object-cover', appearance.themeId === '8-bit-nostalgia' && '[image-rendering:pixelated]')}
      />
    </div>
  );
};

const NumberTileFace = ({ value, surface }: { value: number; surface: string }) => {
  const exponent = Math.log2(value);
  return (
    <div
      data-tile-value={value}
      aria-hidden="true"
      className={cn('absolute inset-0 flex flex-col items-center justify-center overflow-hidden rounded-md font-bold tabular-nums shadow-sm', surface)}
    >
      {value === 131072 && (
        <span className="pointer-events-none absolute inset-2 rounded-full border border-tile-ink/30 shadow-[inset_0_0_0_3px_#FFFFFF,inset_0_0_0_4px_#000000] animate-tile-heartbeat-halo motion-reduce:animate-none" />
      )}
      <span className={cn(
        'relative leading-none',
        value >= 16384 ? 'text-3xl sm:text-5xl' : value >= 1024 ? 'text-xl sm:text-3xl' : 'text-2xl sm:text-4xl',
        value === 65536 && 'rounded bg-black/80 px-2 py-1.5 sm:px-3 sm:py-2',
        value === 131072 && 'rounded-full bg-tile-paper px-1 font-black animate-tile-heartbeat motion-reduce:animate-none'
      )}>
        {value >= 16384 ? <>2<sup className="ml-0.5 text-[0.48em]">{exponent}</sup></> : value}
      </span>
      {value === 8192 && <span className="mt-1 text-xs font-semibold leading-none sm:mt-2 sm:text-base">2<sup>13</sup></span>}
    </div>
  );
};
